import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { trackFocus, getActiveWindow, Dimension } from 'vscode/vscode/vs/base/browser/dom';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { Disposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { MicrotaskDelay } from 'vscode/vscode/vs/base/common/symbols';
import './media/terminalChatWidget.css.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { InlineChatWidget } from 'vscode/vscode/vs/workbench/contrib/inlineChat/browser/inlineChatWidget';
import { TerminalChatContextKeys, MENU_TERMINAL_CHAT_INPUT, MENU_TERMINAL_CHAT_WIDGET, MENU_TERMINAL_CHAT_WIDGET_STATUS, MENU_TERMINAL_CHAT_WIDGET_FEEDBACK } from './terminalChat.js';
import { TerminalStickyScrollContribution } from '../../stickyScroll/browser/terminalStickyScrollContribution.js';

const _moduleId = "vs/workbench/contrib/terminalContrib/chat/browser/terminalChatWidget";
let TerminalChatWidget = class TerminalChatWidget extends Disposable {
    get inlineChatWidget() { return this._inlineChatWidget; }
    constructor(_terminalElement, _instance, _xterm, _instantiationService, _contextKeyService) {
        super();
        this._terminalElement = _terminalElement;
        this._instance = _instance;
        this._xterm = _xterm;
        this._instantiationService = _instantiationService;
        this._contextKeyService = _contextKeyService;
        this._focusedContextKey = TerminalChatContextKeys.focused.bindTo(this._contextKeyService);
        this._visibleContextKey = TerminalChatContextKeys.visible.bindTo(this._contextKeyService);
        this._container = document.createElement('div');
        this._container.classList.add('terminal-inline-chat');
        _terminalElement.appendChild(this._container);
        this._inlineChatWidget = this._instantiationService.createInstance(InlineChatWidget, ChatAgentLocation.Terminal, {
            inputMenuId: MENU_TERMINAL_CHAT_INPUT,
            widgetMenuId: MENU_TERMINAL_CHAT_WIDGET,
            statusMenuId: {
                menu: MENU_TERMINAL_CHAT_WIDGET_STATUS,
                options: {
                    buttonConfigProvider: action => {
                        if (action.id === "workbench.action.terminal.chat.viewInChat"  || action.id === "workbench.action.terminal.chat.runCommand"  || action.id === "workbench.action.terminal.chat.runFirstCommand" ) {
                            return { isSecondary: false };
                        }
                        else {
                            return { isSecondary: true };
                        }
                    }
                }
            },
            feedbackMenuId: MENU_TERMINAL_CHAT_WIDGET_FEEDBACK,
            telemetrySource: 'terminal-inline-chat',
            rendererOptions: { editableCodeBlock: true }
        });
        this._register(Event.any(this._inlineChatWidget.onDidChangeHeight, this._instance.onDimensionsChanged, Event.debounce(this._xterm.raw.onCursorMove, () => void 0, MicrotaskDelay))(() => this._relayout()));
        const observer = ( (new ResizeObserver(() => this._relayout())));
        observer.observe(this._terminalElement);
        this._register(toDisposable(() => observer.disconnect()));
        this._reset();
        this._container.appendChild(this._inlineChatWidget.domNode);
        this._focusTracker = this._register(trackFocus(this._container));
        this.hide();
    }
    _relayout() {
        if (this._dimension) {
            this._doLayout(this._inlineChatWidget.contentHeight);
        }
    }
    _doLayout(heightInPixel) {
        const xtermElement = this._xterm.raw.element;
        if (!xtermElement) {
            return;
        }
        const style = getActiveWindow().getComputedStyle(xtermElement);
        const xtermPadding = parseInt(style.paddingLeft) + parseInt(style.paddingRight);
        const width = Math.min(640, xtermElement.clientWidth - 12  - 2  - 10  - xtermPadding);
        const height = Math.min(480, heightInPixel, this._getTerminalWrapperHeight() ?? Number.MAX_SAFE_INTEGER);
        if (width === 0 || height === 0) {
            return;
        }
        this._container.style.paddingLeft = style.paddingLeft;
        this._dimension = ( (new Dimension(width, height)));
        this._inlineChatWidget.layout(this._dimension);
        this._updateVerticalPosition();
    }
    _reset() {
        this._inlineChatWidget.placeholder = ( localizeWithPath(_moduleId, 0, "Ask how to do something in the terminal"));
        this._inlineChatWidget.updateInfo(( localizeWithPath(_moduleId, 1, "AI-generated commands may be incorrect")));
    }
    reveal() {
        this._doLayout(this._inlineChatWidget.contentHeight);
        this._container.classList.remove('hide');
        this._focusedContextKey.set(true);
        this._visibleContextKey.set(true);
        this._inlineChatWidget.focus();
        this._instance.scrollToBottom();
    }
    _updateVerticalPosition() {
        const font = this._instance.xterm?.getFont();
        if (!font?.charHeight) {
            return;
        }
        const terminalWrapperHeight = this._getTerminalWrapperHeight() ?? 0;
        const cellHeight = font.charHeight * font.lineHeight;
        const topPadding = terminalWrapperHeight - (this._instance.rows * cellHeight);
        const cursorY = (this._instance.xterm?.raw.buffer.active.cursorY ?? 0) + 1;
        const top = topPadding + cursorY * cellHeight;
        this._container.style.top = `${top}px`;
        const widgetHeight = this._inlineChatWidget.contentHeight;
        if (!terminalWrapperHeight) {
            return;
        }
        if (top > terminalWrapperHeight - widgetHeight) {
            this._setTerminalOffset(top - (terminalWrapperHeight - widgetHeight));
        }
        else {
            this._setTerminalOffset(undefined);
        }
    }
    _getTerminalWrapperHeight() {
        return this._terminalElement.clientHeight;
    }
    hide() {
        this._container.classList.add('hide');
        this._reset();
        this._inlineChatWidget.updateChatMessage(undefined);
        this._inlineChatWidget.updateFollowUps(undefined);
        this._inlineChatWidget.updateProgress(false);
        this._inlineChatWidget.updateToolbar(false);
        this._inlineChatWidget.reset();
        this._focusedContextKey.set(false);
        this._visibleContextKey.set(false);
        this._inlineChatWidget.value = '';
        this._instance.focus();
        this._setTerminalOffset(undefined);
    }
    _setTerminalOffset(offset) {
        if (offset === undefined || this._container.classList.contains('hide')) {
            this._terminalElement.style.position = '';
            this._terminalElement.style.bottom = '';
            TerminalStickyScrollContribution.get(this._instance)?.hideUnlock();
        }
        else {
            this._terminalElement.style.position = 'relative';
            this._terminalElement.style.bottom = `${offset}px`;
            TerminalStickyScrollContribution.get(this._instance)?.hideLock();
        }
    }
    focus() {
        this._inlineChatWidget.focus();
    }
    hasFocus() {
        return this._inlineChatWidget.hasFocus();
    }
    input() {
        return this._inlineChatWidget.value;
    }
    addToHistory(input) {
        this._inlineChatWidget.addToHistory(input);
        this._inlineChatWidget.saveState();
        this._inlineChatWidget.value = input;
        this._inlineChatWidget.selectAll(true);
    }
    setValue(value) {
        this._inlineChatWidget.value = value ?? '';
    }
    acceptCommand(code, shouldExecute) {
        this._instance.runCommand(code, shouldExecute);
        this.hide();
    }
    updateProgress(progress) {
        this._inlineChatWidget.updateProgress(progress?.kind === 'markdownContent');
    }
    get focusTracker() {
        return this._focusTracker;
    }
};
TerminalChatWidget = ( (__decorate([
    ( (__param(3, IInstantiationService))),
    ( (__param(4, IContextKeyService)))
], TerminalChatWidget)));

export { TerminalChatWidget };
